import { useState, createContext } from "react"

import useEstimateCache from "../../../api/estimate/cache/estimateCache"
import analyticsEstimate from "../../../api/estimate/csAnalyticsEstimateApi"
import type { EstimateSuccessResult } from "../../../api/estimate/csComEst"
import type { ManufactureCourse } from "../constant/manufactureCourse"
import MANUFACTURE_COURSE from "../constant/manufactureCourse"
import fillRestWithInitialValues from "../service/fillRestWithInitialValues"
import type EstimateValues from "../types/params"
import type { EstimateMeta } from "../types/params"

const listener = (e: BeforeUnloadEvent) => {
  e.preventDefault()
  e.returnValue = ""
}

const useSaveEstimate = () => {
  const [values, setValues] = useState(() => fillRestWithInitialValues({}))
  const [manufactureCourse, setManufactureCourse] = useState<ManufactureCourse>(MANUFACTURE_COURSE.normal)
  const [title, setTitle] = useState("")
  const [remarks, setRemarks] = useState("")
  const [files, setFiles] = useState<File[]>([])
  const [estimateSuccessResult, setEstimateSuccessResult] = useState<EstimateSuccessResult | null>(null)
  const [wasEstimateSaved, setWasEstimateSaved] = useState(false)
  const [bomSnapshotId, setBomSnapshotId] = useState<string | null>(null)
  const [deliveryZeroFlg, setDeliveryZeroFlg] = useState<boolean>(false)
  const [mountQuickFlg, setMountQuickFlg] = useState<boolean>(false)

  const estimateCache = useEstimateCache()

  const startSaveProcess = ({
    values,
    manufactureCourse,
    estimateSuccessResult,
    meta,
    bomSnapshotId,
    deliveryZeroFlg,
    mountQuickFlg,
  }: {
    values: EstimateValues
    manufactureCourse: ManufactureCourse
    estimateSuccessResult: EstimateSuccessResult
    meta: Pick<EstimateMeta, "subject">
    bomSnapshotId: string | null
    deliveryZeroFlg: boolean
    mountQuickFlg: boolean
  }) => {
    setValues(values)
    setManufactureCourse(manufactureCourse)
    setEstimateSuccessResult(estimateSuccessResult)
    // Quadceptで入力した件名はクエリパラメータを通してsubjectに入るので、その値を初期値として設定します。
    setTitle(meta.subject)
    setRemarks("")
    setFiles([])
    setWasEstimateSaved(false)
    setBomSnapshotId(bomSnapshotId)
    setDeliveryZeroFlg(deliveryZeroFlg)
    setMountQuickFlg(mountQuickFlg)
    window.addEventListener("beforeunload", listener)
  }

  const saveComplete = (receiptNo: string) => {
    analyticsEstimate(receiptNo).then(() => {})
    setWasEstimateSaved(true)
    estimateCache.clear()
    window.removeEventListener("beforeunload", listener)
  }

  return {
    values,
    manufactureCourse,
    estimateSuccessResult,
    setEstimateSuccessResult,
    title,
    setTitle,
    remarks,
    setRemarks,
    files,
    setFiles,
    wasEstimateSaved,
    startSaveProcess,
    saveComplete,
    bomSnapshotId,
    deliveryZeroFlg,
    mountQuickFlg,
  }
}

export const SaveEstimateContext = createContext<ReturnType<typeof useSaveEstimate>>(null as any)
export const SaveEstimateProvider = ({ children }: { children: React.ReactNode }) => {
  const value = useSaveEstimate()
  return <SaveEstimateContext.Provider value={value}>{children}</SaveEstimateContext.Provider>
}
